/**
 * Pure model for the agency « Lancer » dialog (LaunchDialog, lot B step 13).
 * No React, no API calls.
 *
 * NOTE: Response types should be imported from `@traaker/shared types/prompt-launch.ts`
 * once lot G step 20 ships.  Until then they are stubbed here.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props launchModel.ts
 * @see docs/plans/prompt-studio-build/00b-dispatch-cadence-quota.md DQ13
 */

import type { BrandQueryAxis } from '@traaker/shared';
import { AXIS_ORDER } from './promptStudioModel';

// ---------------------------------------------------------------------------
// Stub types (replace with @traaker/shared imports once lot G ships)
// ---------------------------------------------------------------------------

/** LOT G step 20: import PromptLaunchPreview from @traaker/shared */
export interface PromptLaunchPreview {
  prompts:         number;
  runs:            number;
  credits:         number;
  balance:         number;
  inFlightRuns:    number;
  replacesToday?:  { axes: BrandQueryAxis[]; runs: number };
}

/** LOT G step 20: import PromptLaunchCreated from @traaker/shared */
export interface PromptLaunchCreated {
  launchId:  string;
  auditIds:  string[];
}

/** LOT G step 20: import PromptLaunchStatus from @traaker/shared */
export interface PromptLaunchStatus {
  status:         'open' | 'closed' | 'refused';
  refusedReason?: PromptBlockReason;
  axes: Partial<Record<BrandQueryAxis, {
    auditId:        string;
    expected?:      number;
    completedCount: number;
    ok:             number;
    failed:         number;
    finished:       boolean;
    retrying:       boolean;
  }>>;
}

/** LOT G step 20: import PromptBlockReason from @traaker/shared */
export type PromptBlockReason =
  | 'billing' | 'trial_expired' | 'strategy' | 'no_engines' | 'nothing_to_run'
  | 'quota_exceeded' | 'quota_unavailable' | 'insufficient_credits'
  | 'planner_failed' | 'tenant_legacy' | 'launch_in_progress'
  | 'enqueue_failed' | 'selection_too_large';

// ---------------------------------------------------------------------------
// LaunchState
// ---------------------------------------------------------------------------

export type ManualLaunchMode = 'all' | 'missing';

export interface LaunchState {
  phase: 'loading' | 'choose' | 'confirmReplace' | 'submitting' | 'progress' | 'legacy';
  previews:    Partial<Record<ManualLaunchMode, PromptLaunchPreview>>;
  previewFailed: boolean;
  mode:        ManualLaunchMode | null;
  idem:        { key: string; mode: ManualLaunchMode; confirmReplace: boolean } | null;
  launchId?:   string;
  status?:     PromptLaunchStatus;
  adopted?:    boolean;
  error?:      'failed' | 'INSUFFICIENT_CREDITS' | 'NOTHING_TO_RUN'
               | 'STRATEGY_NOT_VALIDATED' | 'BILLING_NOT_ACTIVE' | 'NO_ENGINES';
  credits402?: { needed: number; available: number };
}

// ---------------------------------------------------------------------------
// LaunchAction
// ---------------------------------------------------------------------------

export type LaunchAction =
  | { type: 'previewsLoaded'; previews: Partial<Record<ManualLaunchMode, PromptLaunchPreview>>; inProgressLaunchId?: string }
  | { type: 'previewsFailed'; code?: string }
  | { type: 'pick'; mode: ManualLaunchMode }
  | { type: 'submit'; freshKey: string }
  | { type: 'confirmReplace'; freshKey: string }
  | { type: 'back' }
  | { type: 'created'; launchId: string }
  | { type: 'createFailed'; err: any }
  | { type: 'polled'; status: PromptLaunchStatus }
  | { type: 'pollFailed' };

// ---------------------------------------------------------------------------
// launchReducer
// ---------------------------------------------------------------------------

export function launchReducer(s: LaunchState, a: LaunchAction): LaunchState {
  switch (a.type) {
    case 'previewsLoaded': {
      if (a.inProgressLaunchId) {
        return {
          ...s,
          phase: 'progress',
          previews: a.previews,
          launchId: a.inProgressLaunchId,
          adopted: true,
        };
      }
      // Default mode: 'missing' when it has runs, else 'all'
      const missingRuns = a.previews.missing?.runs ?? 0;
      const mode: ManualLaunchMode = missingRuns > 0 ? 'missing' : 'all';
      return { ...s, phase: 'choose', previews: a.previews, mode };
    }

    case 'previewsFailed': {
      if (a.code === 'LAUNCH_IN_PROGRESS') {
        // Can't extract launchId from code string — show progress with no id
        return { ...s, phase: 'progress', adopted: true };
      }
      if (a.code === 'PROMPT_DISPATCH_LEGACY') {
        return { ...s, phase: 'legacy' };
      }
      return { ...s, phase: 'choose', previewFailed: true };
    }

    case 'pick': {
      if (a.mode === 'missing' && (s.previews.missing?.runs ?? 0) === 0) {
        return s; // no-op: disabled at 0
      }
      return { ...s, mode: a.mode };
    }

    case 'submit': {
      if (s.phase !== 'choose' || !s.mode) return s;

      const preview = s.previews[s.mode];
      if (s.mode === 'all' && preview?.replacesToday) {
        // Need confirmation first
        return {
          ...s,
          phase: 'confirmReplace',
          idem: { key: a.freshKey, mode: s.mode, confirmReplace: false },
        };
      }
      return {
        ...s,
        phase: 'submitting',
        idem: { key: a.freshKey, mode: s.mode, confirmReplace: false },
      };
    }

    case 'confirmReplace': {
      if (s.phase !== 'confirmReplace') return s;
      return {
        ...s,
        phase: 'submitting',
        idem: s.idem
          ? { ...s.idem, key: a.freshKey, confirmReplace: true }
          : { key: a.freshKey, mode: s.mode ?? 'all', confirmReplace: true },
      };
    }

    case 'back': {
      return { ...s, phase: 'choose' };
    }

    case 'created': {
      return { ...s, phase: 'progress', launchId: a.launchId };
    }

    case 'createFailed': {
      const err = a.err;
      const status: number  = err?.status;
      const code:   string  = err?.body?.code ?? '';

      if (status === 402) {
        return {
          ...s,
          phase: 'choose',
          error: 'INSUFFICIENT_CREDITS',
          credits402: { needed: err.body.needed, available: err.body.available },
          idem: null,
        };
      }
      if (code === 'LAUNCH_IN_PROGRESS') {
        return { ...s, phase: 'progress', launchId: err?.body?.launchId, adopted: true };
      }
      if (code === 'PROMPT_DISPATCH_LEGACY') {
        return { ...s, phase: 'legacy' };
      }
      if (code === 'NOTHING_TO_RUN') {
        return { ...s, phase: 'choose', error: 'NOTHING_TO_RUN', idem: null };
      }
      if (code === 'STRATEGY_NOT_VALIDATED') {
        return { ...s, phase: 'choose', error: 'STRATEGY_NOT_VALIDATED', idem: null };
      }
      if (code === 'BILLING_NOT_ACTIVE') {
        return { ...s, phase: 'choose', error: 'BILLING_NOT_ACTIVE', idem: null };
      }
      if (code === 'NO_ENGINES') {
        return { ...s, phase: 'choose', error: 'NO_ENGINES', idem: null };
      }
      if (code === 'CONFIRM_REPLACE_REQUIRED') {
        // Day got a launch since the preview — reload previews
        return { ...s, phase: 'loading' };
      }

      // Network / 5xx — keep idem, show failed
      return { ...s, phase: 'choose', error: 'failed' };
    }

    case 'polled': {
      return { ...s, status: a.status };
    }

    case 'pollFailed': {
      // Keep last status, polling continues
      return s;
    }

    default:
      return s;
  }
}

/**
 * A 409 `PROMPT_DISPATCH_LEGACY` (the tenant is not on the DynamoDB scheduler yet,
 * 00b DQ15): the axis page hands back to its per-axis dialog (rollout fallback).
 */
export function isLegacyDispatch(err: unknown): boolean {
  const e = err as { status?: number; body?: { code?: string } } | undefined;
  return e?.status === 409 && e.body?.code === 'PROMPT_DISPATCH_LEGACY';
}

/** One preview call, settled: its answer, or the error it threw. */
export interface PreviewSettled {
  value: PromptLaunchPreview | null;
  err?: unknown;
}

/**
 * What the two parallel previews lead to. `handBack` = a legacy 409 while the caller
 * has a fallback (`canHandBack`, the axis page's per-axis dialog); otherwise a failure
 * of both carries the 409 code (`PROMPT_DISPATCH_LEGACY` → `.launch.legacy`,
 * `LAUNCH_IN_PROGRESS` → progress), and any answer loads the choice.
 */
export function previewOutcome(
  all: PreviewSettled,
  missing: PreviewSettled,
  canHandBack: boolean,
):
  | { kind: 'handBack' }
  | { kind: 'failed'; code?: string }
  | { kind: 'loaded'; previews: Partial<Record<ManualLaunchMode, PromptLaunchPreview>> } {
  if (canHandBack && (isLegacyDispatch(all.err) || isLegacyDispatch(missing.err))) return { kind: 'handBack' };
  if (!all.value && !missing.value) {
    const code = [all.err, missing.err]
      .map((e) => (e as { body?: { code?: string } } | undefined)?.body?.code)
      .find(Boolean);
    return { kind: 'failed', code };
  }
  const previews: Partial<Record<ManualLaunchMode, PromptLaunchPreview>> = {};
  if (all.value) previews.all = all.value;
  if (missing.value) previews.missing = missing.value;
  return { kind: 'loaded', previews };
}

// ---------------------------------------------------------------------------
// canLaunch
// ---------------------------------------------------------------------------

/**
 * True when the launch button should be enabled.
 * UI-SPEC §2.27.
 */
export function canLaunch(s: LaunchState): boolean {
  if (s.phase !== 'choose' || !s.mode) return false;
  const preview = s.previews[s.mode];
  if (!preview) return false;
  if (preview.runs <= 0) return false;
  if (preview.inFlightRuns > 0) return false;
  if (preview.credits > preview.balance) return false;
  return true;
}

// ---------------------------------------------------------------------------
// launchAxes
// ---------------------------------------------------------------------------

/**
 * AXIS_ORDER over the axes in the launch status.
 */
export function launchAxes(
  st: PromptLaunchStatus,
): Array<{
  axis:     BrandQueryAxis;
  done:     number;
  total:    number | null;
  ok:       number;
  failed:   number;
  finished: boolean;
  retrying: boolean;
}> {
  return AXIS_ORDER
    .filter((ax) => ax in (st.axes ?? {}))
    .map((ax) => {
      const a = st.axes[ax]!;
      return {
        axis:     ax,
        done:     a.completedCount,
        total:    a.expected ?? null,
        ok:       a.ok,
        failed:   a.failed,
        finished: a.finished,
        retrying: a.retrying,
      };
    });
}

// ---------------------------------------------------------------------------
// launchClosed / refusedKey
// ---------------------------------------------------------------------------

/** Polling stops when status is 'closed' or 'refused'. */
export function launchClosed(st: PromptLaunchStatus): boolean {
  return st.status === 'closed' || st.status === 'refused';
}

/**
 * Map a PromptBlockReason to its UI-SPEC §4.12 i18n key suffix.
 */
export function refusedKey(r: PromptBlockReason | undefined): string {
  if (!r) return '.other';
  if (r === 'billing' || r === 'trial_expired') return '.launch.refused.billing';
  if (r === 'insufficient_credits')             return '.credits';
  if (r === 'strategy')                         return '.strategy';
  if (r === 'no_engines')                       return '.engines';
  if (r === 'nothing_to_run')                   return '.nothing';
  if (r === 'launch_in_progress')               return '.inProgress';
  return '.other';
}
